import { format } from "date-fns";
import {
  FilterActionTypes,
  type State,
  action,
  computed,
  effectOn,
  persist,
  thunk,
} from "easy-peasy";
import difference from "lodash/difference";
import isEqual from "lodash/isEqual";
import union from "lodash/union";

import allAvailabilitySearchModel from "./allAvailabilitySearch";
import firebaseAllAvailabilityModel from "./firebaseAllAvailability";
import firebaseRegularTableSyncModel from "./firebaseRegularTableSync";
import { REGION_IDS, SHOW_25_DISCOUNT } from "../dependencies/env";
import { Logger } from "../dependencies/logger";
import { persistenceStorage } from "../dependencies/storage";
import {
  DATE_FORMAT,
  isNonEmpty,
  removeSessionSelectedRestaurantId,
  removeSessionViewState,
  sessionsMatch,
  wait,
} from "../dependencies/utils";
import {
  type DiscountAvailabilityFilterContext,
  buildDiscountDatesByIdForFetch,
  buildFirebaseAllAvailabilitySessionsById,
  clearDeriveAvailabilityCaches,
  deriveDiscountAvailabilityBundle,
  filterIdsForDiscountVisibility,
  firstTableDiscountAvailabilityStrategy,
  getFirebaseAvailabilityPathMode,
  getMissingFirebaseAllAvailabilityIds,
  isDiscountVisible,
  shouldShowDiscountSection,
} from "../utils/discountAvailability";
import {
  AbortError,
  batchedThunk,
  createLogEffect,
  isAbortError,
} from "../utils/store";
import {
  beginListInitialLoad,
  cancelListInitialLoad,
  failListInitialLoad,
  settleListInitialLoad,
} from "../utils/store/listPerformance";
import { DEFAULT_REGULAR_DATES_TO_SHOW } from "./date/defaults";
import getFilterDates from "./date/getFilterDates";
import dateModel from "./date/model";
import discountOnlyModel from "./discountOnly/model";
import favouriteModel from "./favourite/model";
import hasLastTableModel from "./hasLastTable/model";
import nearToModel from "./nearTo/model";
import pageModel from "./page/model";
import peopleModel from "./people/model";
import regularOnlyModel from "./regularOnly/model";
import allIdsModel from "./restaurants/allIds";
import byIdModel from "./restaurants/byId";
import getQuerySessions from "./session/getQuerySessions";
import { type AvailabilitySearchSlotWithOptions } from "../gql/getters/allAvailabilitySearch";
import type { NonEmptyArray } from "../types";
import restaurantStatusesModel from "./restaurantStatuses/model";
import sessionModel from "./session/model";
import sortModel from "./sort/model";
import sortOptions from "./sort/options";
import resolveSortParam from "./sort/resolveSortParam";
import suburbModel from "./suburb/model";
import tagIdsModel from "./tagIds/model";
import isInTime from "./times/isInTime";
import timesModel from "./times/model";
import type {
  ByDate,
  ByRestaurantId,
  RegularAvailabilityByDate,
  RegularAvailabilityById,
  RegularAvailabilitySlotType,
  RestaurantToShow,
  StoreModel,
} from "./types";
import walkInModel from "./walkIn/model";
import {
  shouldShowWalkInDiscountSection,
  walkInDiscountAvailabilityStrategy,
} from "./walkIn/utils";

const logEffect = (
  property: keyof FilterActionTypes<StoreModel>,
  enabled = false,
) =>
  createLogEffect<StoreModel>("RESTAURANT_STORE", property, enabled || false);

const logger = new Logger("RESTAURANT_STORE", "LOG");
// logger.enable();

const MINIMUM_RESULTS = 5;

/** List-mode all-ids fetch is allowed for this store snapshot. */
const isListFetchActive = (state: State<StoreModel>) => {
  if (!state.enabled || state.mode === "single" || state.regionId === null) {
    return false;
  }

  const favourite = state.favourite.value;
  if (favourite !== false && favourite.length === 0) {
    return false;
  }

  if (state.walkIn.enabled && state.walkIn.clockRegionId !== state.regionId) {
    return false;
  }

  return true;
};

const buildDiscountAvailabilityFilterContext = (
  sessions: SessionType[],
  hasLastTable: boolean,
  timesStart: StoreModel["times"]["start"],
  timesEnd: StoreModel["times"]["end"],
  people: number,
): DiscountAvailabilityFilterContext => ({
  sessions,
  hasLastTable,
  timesStart,
  timesEnd,
  showAllDiscounts: SHOW_25_DISCOUNT === "true",
  people,
});

const model: StoreModel = {
  name: "Unnamed Restaurant Store",
  // Overwritten per instance in {@link createRestaurantStore}.
  deriveCacheIdentity: Object.create(null) as object,

  // #region - Enabled
  enabled: false,
  enabledOnChange: effectOn([(state) => state.enabled], (_actions, change) => {
    const prev = change.prev[0];
    const current = change.current[0];
    logger.log(`Enabled changed from "${prev}" to "${current}"`);
  }),
  enable: action((state) => {
    state.enabled = true;
  }),
  disable: action((state) => {
    state.enabled = false;
  }),
  // #endregion - Enabled

  // #region - Mode
  mode: undefined,
  modeOnChange: effectOn(
    [(state) => state.mode],
    (_actions, change, { getState }) => {
      const prev = change.prev[0];
      const current = change.current[0];
      logger.log(`Mode changed from "${prev}" to "${current}"`);

      const store = getState().deriveCacheIdentity;

      // `onInputsChange` does not re-run for map ↔ list, so a map fetch's clock
      // would otherwise be settled later as a list load. Drop it on the way out
      // and start again on the way in.
      if (current !== "list") {
        cancelListInitialLoad(store);
        return;
      }

      beginListInitialLoad(store);
    },
  ),
  setMode: action((state, mode) => {
    state.mode = mode;
  }),
  // #endregion - Mode

  // #region - Inputs
  // Region
  regionId: null,
  regionIdOnChange: logEffect("regionId"),
  setRegionId: action((state, payload) => {
    logger.log("setRegionId", payload);

    if (state.regionId === payload) {
      return;
    }

    state.regionId = payload;
  }),
  resetOnRegionChange: effectOn(
    [(state) => state.regionId],
    (actions, change) => {
      const prevRegionId = change.prev[0];
      const currentRegionId = change.current[0];

      if (prevRegionId !== null && prevRegionId !== currentRegionId) {
        actions.clearInViewById();
        actions.allIds.reset();
        actions.page.resetPage();

        // reset the saved map position
        removeSessionSelectedRestaurantId();
        removeSessionViewState();
      }
    },
  ),
  discoverEnabled: computed((state) =>
    REGION_IDS.some((discoverRegionId) => discoverRegionId === state.regionId),
  ),
  discoverEnabledOnChange: logEffect("discoverEnabled"),

  // Discount dates to show
  discountDatesToShow: [format(new Date(), DATE_FORMAT)], // start with today as default (updated based on filters)
  discountDatesToShowOnChange: logEffect("discountDatesToShow"),
  setDiscountDatesToShow: action((state, payload) => {
    logger.log("setDiscountDatesToShow", payload);

    // If the payload is equal to the current state.discountDatesToShow, do nothing
    if (
      payload.length === state.discountDatesToShow.length &&
      payload.every((date, idx) => date === state.discountDatesToShow[idx])
    ) {
      return;
    }

    state.discountDatesToShow = payload;
  }),
  featuredSortVariant: sortOptions.FEATURED_NEW_TOP,
  setFeaturedSortVariant: action((state, payload) => {
    logger.log("setFeaturedSortVariant", payload);
    state.featuredSortVariant = payload;
  }),
  skipInitialFirebaseFetch: false,
  setSkipInitialFirebaseFetch: action((state, payload) => {
    logger.log("setSkipInitialFirebaseFetch", payload);

    if (state.skipInitialFirebaseFetch === payload) {
      return;
    }

    state.skipInitialFirebaseFetch = payload;
  }),
  /**
   * Walk-in always uses today (first discount date) without mutating
   * {@link StoreModel.date}; First Table uses the date filter as-is.
   */
  effectiveDate: computed(
    [
      (state) => state.walkIn.enabled,
      (state) => state.date.value,
      (state) => state.discountDatesToShow,
    ],
    (walkInEnabled, date, discountDatesToShow) =>
      walkInEnabled ? discountDatesToShow[0] : date,
  ),
  effectiveDiscountDatesToShow: computed(
    [(state) => state.effectiveDate, (state) => state.discountDatesToShow],
    (date, discountDatesToShow) => {
      if (date === "week") {
        return discountDatesToShow;
      }

      if (discountDatesToShow.includes(date)) {
        return [date];
      }

      return discountDatesToShow;
    },
  ),
  additionalDaysToShow: 0,
  additionalDaysToShowOnChange: logEffect("additionalDaysToShow"),
  setAdditionalDaysToShow: action((state, payload) => {
    if (state.additionalDaysToShow === payload) {
      return;
    }
    state.additionalDaysToShow = payload;
  }),
  rolloverAdditionalDaysToShow: 0,
  rolloverAdditionalDaysToShowOnChange: logEffect(
    "rolloverAdditionalDaysToShow",
  ),
  setRolloverAdditionalDaysToShow: action((state, payload) => {
    if (state.rolloverAdditionalDaysToShow === payload) {
      return;
    }
    state.rolloverAdditionalDaysToShow = payload;
  }),

  // Regular dates to show
  regularDatesToShow: DEFAULT_REGULAR_DATES_TO_SHOW, // start with 10 days as default (updated based on filters)
  regularDatesToShowOnChange: logEffect("regularDatesToShow"),
  setRegularDatesToShow: action((state, payload) => {
    logger.log("setRegularDatesToShow", payload);
    // If the payload is equal to the current state.regularDatesToShow, do nothing
    if (
      payload.length === state.regularDatesToShow.length &&
      payload.every((date, idx) => date === state.regularDatesToShow[idx])
    ) {
      return;
    }

    state.regularDatesToShow = payload;
  }),

  // Filters
  session: sessionModel,
  date: dateModel,
  people: peopleModel,
  discountOnly: persist(discountOnlyModel, {
    allow: ["value"],
    storage: persistenceStorage,
  }),
  favourite: favouriteModel,
  regularOnly: regularOnlyModel,
  tagIds: tagIdsModel,
  suburb: suburbModel,
  hasLastTable: hasLastTableModel,
  times: timesModel,
  nearTo: nearToModel,
  restaurantStatuses: restaurantStatusesModel,
  walkIn: walkInModel,

  // Sort
  sort: persist(sortModel, { allow: ["value"], storage: persistenceStorage }),
  // #endregion - Inputs

  // #region - In view
  inViewById: {},
  inViewByIdOnChange: logEffect("inViewById", false),
  clearInViewById: action((state) => {
    state.inViewById = {};
  }),
  mergeInView: action((state, payload) => {
    for (const { inView, restaurantId } of payload) {
      if (inView) {
        state.inViewById[restaurantId] = true;
      } else {
        delete state.inViewById[restaurantId];
      }
    }
  }),
  setInView: batchedThunk((actions, payloads) => {
    actions.mergeInView(payloads);
  }, 100),
  // #endregion - In view

  // #region - Restaurants
  allIds: allIdsModel,

  onInputsChange: effectOn(
    [
      (state) => state.enabled,
      (state) => state.mode === "single",
      (state) => state.regionId,
      (state) => state.suburb.ids,
      (state) => state.session.value,
      (state) => state.effectiveDate,
      (state) => state.favourite.value,
      (state) => state.regularOnly.value,
      (state) => state.hasLastTable.value,
      (state) => state.effectiveDiscountDatesToShow,
      (state) => state.tagIds.value,
      (state) => state.sort.value,
      (state) =>
        state.sort.value === "NEAREST" ? state.nearTo.location : null,
      (state) => state.people.value,
      (state) => state.restaurantStatuses.value,
      (state) => state.featuredSortVariant,
      (state) => state.walkIn.enabled,
      // Not `walkIn.time` — that ticks every minute. This only moves when the
      // clock starts being read in a different city.
      (state) => state.walkIn.clockRegionId,
    ],
    (actions, change, { getState }) => {
      const abortIfListInactive = () => {
        if (!isListFetchActive(getState())) {
          actions.allIds.abort();
        }
      };

      const enabled = change.current[0];
      if (!enabled) {
        return abortIfListInactive;
      }

      const isSingleMode = change.current[1];
      if (isSingleMode) {
        return abortIfListInactive;
      }
      const mode = getState().mode;

      const regionId = change.current[2];
      if (regionId === null) {
        return abortIfListInactive;
      }

      const suburbIds = change.current[3];
      const session = change.current[4];
      const date = change.current[5];
      const favourite = change.current[6];

      // if the favourite filter is active but the user hasn't favourited any restaurants then show an empty list
      if (favourite !== false && favourite.length === 0) {
        actions.allIds.reset();
        actions.allIds.set([]);
        // No id request, but the empty list still renders. reset() already
        // aborted any in-flight fetch and cleared previews. Force an empty
        // page so a computed that has not dropped the previous ids cannot
        // classify this attempt as having availability. A map with no
        // favourites must not open an attempt; entering list mode starts one.
        if (mode === "list") {
          const state = getState();
          beginListInitialLoad(state.deriveCacheIdentity);
          settleListInitialLoad({
            ...state,
            idsToShow: [],
            pagedIds: [],
          });
        }
        return () => {
          cancelListInitialLoad(getState().deriveCacheIdentity);
        };
      }

      const regularOnly = change.current[7];
      const hasLastTable = change.current[8];
      const currentEffectiveDiscountDatesToShow = change.current[9];
      const prevEffectiveDiscountDatesToShow = change.prev[9];
      const tagIds = change.current[10];
      const sort = change.current[11];
      const origin = sort === "NEAREST" ? change.current[12] : null;
      const people = change.current[13];
      const restaurantStatusSelection = change.current[14];
      const featuredSortVariant = change.current[15];
      const walkInEnabled = change.current[16];
      const clockRegionId = change.current[17];
      // Nearest is ordered client-side so the GraphQL query can stay on a
      // cacheable Featured sort instead of a unique NearToInput.
      const querySort =
        sort === "NEAREST"
          ? resolveSortParam(sortOptions.FEATURED_NEW_TOP, featuredSortVariant)
          : resolveSortParam(sort, featuredSortVariant);

      let queryVariables;
      if (walkInEnabled) {
        // The clock still reads in the city the user just left, so its sessions
        // would describe the wrong time of day. Wait for the host to resync;
        // that lands as a `clockRegionId` change and runs this effect again.
        if (clockRegionId !== regionId) {
          return abortIfListInactive;
        }

        // Sample the clock here rather than watching `expectedSessionTypes`, so
        // a meal rollover cannot refetch the list or remount Firebase on its own.
        const walkInSessions = getState().walkIn.expectedSessionTypes;
        actions.walkIn.setActiveSessionTypes(walkInSessions);

        queryVariables = actions.walkIn.buildQueryVariables({
          regionId,
          suburbIds,
          favourite,
          tagIds,
          sort: querySort,
          people,
          status: restaurantStatusSelection,
          discountDatesToShow: currentEffectiveDiscountDatesToShow,
          sessions: walkInSessions,
        });
      } else {
        const querySessions = getQuerySessions([session], hasLastTable);
        queryVariables = {
          ids:
            favourite !== false && favourite.length > 0 ? favourite : undefined, // only fetch favourite ids if they are set
          regionId,
          suburbs: suburbIds,
          sessions:
            date === "week" // don't prioritise by session availability on the week view
              ? undefined
              : querySessions,
          offersSession: querySessions,
          offersFirstTableSession: querySessions,
          dates:
            date === "week" // don't prioritise by date availability on the week view
              ? undefined
              : getFilterDates({
                  date,
                  regularOnly,
                  discountDatesToShow: currentEffectiveDiscountDatesToShow,
                }),
          tags: tagIds,
          sort: querySort,
          people,
          status: restaurantStatusSelection,
        };
      }

      logger.log("onInputsChange", { walkInEnabled });

      const datesLengthChangedByOne =
        currentEffectiveDiscountDatesToShow.length ===
          prevEffectiveDiscountDatesToShow.length + 1 ||
        currentEffectiveDiscountDatesToShow.length + 1 ===
          prevEffectiveDiscountDatesToShow.length;
      const isRolloverChange =
        prevEffectiveDiscountDatesToShow.length > 1 && datesLengthChangedByOne;

      const { allIds } = getState();
      const sameQuery =
        !isRolloverChange && isEqual(allIds.variables, queryVariables);
      const hasReusableResults = allIds.loading || allIds.value.length > 0;
      const originChanged = !isEqual(allIds.origin, origin);

      if (sameQuery && originChanged) {
        // Settled empty success still has a cached query result to re-order.
        // Do not use hasSettled on the origin-unchanged path: failures set it.
        if (hasReusableResults || (allIds.hasSettled && allIds.error == null)) {
          actions.allIds.fetch({ variables: queryVariables, origin });
          return abortIfListInactive;
        }
      } else if (sameQuery && hasReusableResults) {
        return abortIfListInactive;
      }

      if (!isRolloverChange) {
        if (mode !== "map") {
          actions.clearInViewById();
        }
        actions.allIds.reset();
      }

      actions.allIds.fetch({ variables: queryVariables, origin });

      return abortIfListInactive;
    },
  ),

  refetch: thunk((actions, _payload, { getState }) => {
    if (getState().mode === "single") {
      return;
    }

    actions.allIds.refetch();
  }),

  byId: byIdModel,
  fetchRestaurantById: thunk((actions, restaurantId, { getState }) => {
    if (getState().byId.value[restaurantId] != null) {
      return;
    }

    actions.byId.fetch({ ids: [restaurantId] });
  }),

  pagedIds: computed(
    [(state) => state.allIds.value, (state) => state.page.limit],
    (allIds, limit) => allIds.slice(0, limit),
  ),
  onPagedIdsChange: effectOn(
    [(state) => state.pagedIds, (state) => state.mode],
    (actions, change, { getState }) => {
      const pagedIds = change.current[0];
      const mode = change.current[1];
      if (mode === "map") {
        return;
      }

      const previousPagedIds = change.prev[0] ?? [];
      const previousMode = change.prev[1];
      const isPageAppend =
        previousMode !== "map" &&
        previousPagedIds.length > 0 &&
        pagedIds.length >= previousPagedIds.length &&
        previousPagedIds.every((id, index) => pagedIds[index] === id);
      const restaurantsById = getState().byId.value;
      // Appends only request new ids, but also re-queue prior page failures so
      // a soft-fail can recover without waiting for a full filter refresh.
      const idsToFetch = isPageAppend
        ? [
            ...pagedIds
              .slice(0, previousPagedIds.length)
              .filter((id) => restaurantsById[id] === null),
            ...pagedIds.slice(previousPagedIds.length),
          ]
        : pagedIds;

      if (idsToFetch.length === 0) {
        return;
      }

      // Non-append replaces the paged set — cancel every in-flight details
      // fetch, including older prefix-stable ones left alive across appends.
      if (!isPageAppend && previousMode !== "map") {
        actions.byId.abort();
      }

      actions.byId.fetch({ ids: idsToFetch });

      return () => {
        const currentPagedIds = getState().pagedIds;
        const isStillPagedPrefix = pagedIds.every(
          (id, index) => currentPagedIds[index] === id,
        );
        // Prefix broke (filter refresh / non-append). Abort every tracked
        // controller so an orphaned append-era fetch cannot soft-fail later.
        if (getState().mode !== "map" && !isStillPagedPrefix) {
          actions.byId.abort();
        }
      };
    },
  ),

  fetchedIds: computed(
    [
      (state) => state.pagedIds,
      (state) => state.byId.value,
      (state) => state.mode,
      (state) => state.allIds.value,
    ],
    (pagedIds, restaurantsById, mode, allIds) => {
      // Map has no pagination: every pin in the current result whose details
      // have been fetched stays hydrated, so carousel swipe-back re-derives
      // availability from cache instead of only tracking the selected id.
      const candidateIds = mode === "map" ? allIds : pagedIds;
      return candidateIds.filter((id) => restaurantsById[id] != null);
    },
  ),

  discountDatesToShowById: computed(
    [
      (state) => state.fetchedIds,
      (state) => state.byId.value,
      (state) => state.effectiveDiscountDatesToShow,
      (state) => state.additionalDaysToShow,
      (state) => state.rolloverAdditionalDaysToShow,
    ],
    (
      fetchedIds,
      restaurantsById,
      effectiveDiscountDatesToShow,
      additionalDaysToShow,
      rolloverAdditionalDaysToShow,
    ) =>
      fetchedIds.reduce(
        (
          discountDatesToShowById: ByRestaurantId<NonEmptyArray<string>>,
          id,
        ) => {
          const restaurantBookingWindowDays =
            restaurantsById[id]?.bookingWindowDays;
          if (restaurantBookingWindowDays) {
            const truncatedDatesToShow = effectiveDiscountDatesToShow.slice(
              0,
              restaurantBookingWindowDays +
                additionalDaysToShow +
                rolloverAdditionalDaysToShow,
            );

            if (isNonEmpty(truncatedDatesToShow)) {
              discountDatesToShowById[id] = truncatedDatesToShow;
              return discountDatesToShowById;
            }
          }

          discountDatesToShowById[id] = effectiveDiscountDatesToShow;
          return discountDatesToShowById;
        },
        {},
      ),
  ),
  discountDatesToShowByIdOnChange: logEffect("discountDatesToShowById"),
  // #endregion - Restaurants

  // #region - Discount availability
  // #region -- Firebase all availability
  firebaseAllAvailability: firebaseAllAvailabilityModel,
  firebaseAllAvailabilityAbortController: undefined,
  setFirebaseAllAvailabilityAbortController: action(
    (state, abortController) => {
      state.firebaseAllAvailabilityAbortController = abortController;
    },
  ),
  firebaseAllAvailabilityInViewAbortController: undefined,
  setFirebaseAllAvailabilityInViewAbortController: action(
    (state, abortController) => {
      state.firebaseAllAvailabilityInViewAbortController = abortController;
    },
  ),
  firebaseAllAvailabilityReplayAbortController: undefined,
  setFirebaseAllAvailabilityReplayAbortController: action(
    (state, abortController) => {
      state.firebaseAllAvailabilityReplayAbortController = abortController;
    },
  ),
  abortFirebaseAllAvailability: thunk((_actions, _payload, { getState }) => {
    const state = getState();
    state.firebaseAllAvailabilityAbortController?.abort();
    state.firebaseAllAvailabilityInViewAbortController?.abort();
    state.firebaseAllAvailabilityReplayAbortController?.abort();
  }),
  /**
   * Sessions to subscribe on the shared Firebase availability path.
   * Walk-in uses snapshotted {@link WalkInModel.activeSessionTypes}; First Table
   * uses the session filter. Empty only when walk-in is on and the snapshotted
   * clock time was invalid (windows cover the day).
   *
   * Do not read live {@link WalkInModel.expectedSessionTypes} here — that
   * recomputes every `setClock` tick. List mode snapshots on filter commit;
   * {@link snapshotWalkInSessionsForSingleMode} snapshots in `single` mode.
   */
  firebaseAvailabilitySessions: computed(
    [
      (state) => state.walkIn.enabled,
      (state) => state.walkIn.activeSessionTypes,
      (state) => state.session.value,
    ],
    (walkInEnabled, activeSessionTypes, session) =>
      walkInEnabled ? activeSessionTypes : [session],
  ),
  /**
   * `onInputsChange` returns before `setActiveSessionTypes` in `single` mode.
   * Snapshot clock sessions here so Firebase still has a session list, but only
   * when membership changes (`setActiveSessionTypes` no-ops otherwise).
   */
  snapshotWalkInSessionsForSingleMode: effectOn(
    [
      (state) => state.mode,
      (state) => state.walkIn.enabled,
      (state) => state.walkIn.expectedSessionTypes,
    ],
    (actions, change) => {
      const mode = change.current[0];
      const walkInEnabled = change.current[1];
      const expectedSessionTypes = change.current[2];

      if (mode !== "single" || !walkInEnabled) {
        return;
      }

      actions.walkIn.setActiveSessionTypes(expectedSessionTypes);
    },
  ),
  fetchFirebaseAllAvailabilityOnlyOnce: effectOn(
    [
      (state) => state.pagedIds,
      (state) =>
        state.pagedIds.length > 0 &&
        state.pagedIds.every((id) => state.byId.value[id] !== undefined),
      (state) => state.regularOnly.value,
      (state) => state.effectiveDate,
      (state) => state.enabled,
      (state) => state.hasLastTable.value,
      (state) => state.skipInitialFirebaseFetch,
      (state) => state.walkIn.enabled,
      (state) => state.firebaseAvailabilitySessions,
      (state) => state.mode,
    ],
    (actions, change, { getState }) => {
      const ids = change.current[0];
      const allPagedIdsFetched = change.current[1];
      const regularOnly = change.current[2];
      const date = change.current[3];
      const enabled = change.current[4];
      const hasLastTable = change.current[5];
      const skipInitialFirebaseFetch = change.current[6];
      const walkInEnabled = change.current[7];
      const sessions = change.current[8];
      const mode = change.current[9];

      // FT `regularOnly` is inactive on the walk-in path.
      if (
        !enabled ||
        mode === "map" ||
        ids.length === 0 ||
        (!walkInEnabled && regularOnly) ||
        !allPagedIdsFetched ||
        skipInitialFirebaseFetch
      ) {
        return;
      }

      // No sessions (e.g. walk-in clock outside meal windows) → nothing to fetch.
      if (sessions.length === 0) {
        return;
      }

      const effectiveHasLastTable = walkInEnabled ? false : hasLastTable;
      const state = getState();

      const firebaseAllAvailabilityById = state.firebaseAllAvailability.byId;
      const restaurantsById = state.byId.value;
      const missingIds = getMissingFirebaseAllAvailabilityIds(
        ids,
        firebaseAllAvailabilityById,
        sessions,
        date,
        effectiveHasLastTable,
        state.discountDatesToShowById,
        restaurantsById,
        walkInEnabled,
      );
      const idsToFetch = missingIds;

      if (idsToFetch.length === 0) {
        return;
      }

      const onlyOnceById = idsToFetch.reduce(
        (onlyOnceByIdAcc: ByRestaurantId<boolean>, id) => {
          onlyOnceByIdAcc[id] = true;

          return onlyOnceByIdAcc;
        },
        {},
      );
      const sessionsById = buildFirebaseAllAvailabilitySessionsById(
        idsToFetch,
        sessions,
        effectiveHasLastTable,
        restaurantsById,
        walkInEnabled,
      );
      const datesById = buildDiscountDatesByIdForFetch(
        idsToFetch,
        state.discountDatesToShowById,
      );

      const abortController = new AbortController();
      actions.setFirebaseAllAvailabilityAbortController(abortController);

      logger.log(
        "fetchFirebaseAllAvailabilityOnlyOnce - idsToFetch",
        idsToFetch,
      );

      actions.firebaseAllAvailability
        .fetch({
          restaurantIds: idsToFetch,
          sessionsById,
          datesById,
          onlyOnceById,
          pathMode: getFirebaseAvailabilityPathMode(date),
          abortSignal: abortController.signal,
        })
        .then(() => {
          if (
            getState().firebaseAllAvailabilityAbortController ===
            abortController
          ) {
            actions.setFirebaseAllAvailabilityAbortController(undefined);
          }
        });

      return () => {
        const { firebaseAllAvailabilityAbortController } = getState();
        if (
          firebaseAllAvailabilityAbortController !== undefined &&
          !firebaseAllAvailabilityAbortController.signal.aborted
        ) {
          firebaseAllAvailabilityAbortController.abort(
            new AbortError(
              'Canceled "fetchFirebaseAllAvailabilityOnlyOnce" before re-fetching',
            ),
          );
        }
      };
    },
  ),
  replayFirebaseAllAvailabilitySessionSnapshots: effectOn(
    [
      (state) => state.discountDatesToShowById,
      (state) => state.effectiveDate,
      (state) => state.regularOnly.value,
      (state) => state.hasLastTable.value,
      (state) => state.enabled,
      (state) => state.pagedIds,
      (state) => state.walkIn.enabled,
      (state) => state.firebaseAvailabilitySessions,
    ],
    (actions, change, { getState }) => {
      const discountDatesToShowById = change.current[0];
      const date = change.current[1];
      const regularOnly = change.current[2];
      const hasLastTable = change.current[3];
      const enabled = change.current[4];
      const pagedIds = change.current[5];
      const walkInEnabled = change.current[6];
      const sessions = change.current[7];

      // Live in-view sync already refreshes date windows for subscribed paths.
      // This covers off-screen onlyOnce restaurants that still have a cached
      // session snapshot. FT `regularOnly` is inactive on the walk-in path.
      if (
        !enabled ||
        (!walkInEnabled && regularOnly) ||
        date !== "week" ||
        pagedIds.length === 0
      ) {
        return;
      }

      if (sessions.length === 0) {
        return;
      }

      const effectiveHasLastTable = walkInEnabled ? false : hasLastTable;
      const sessionsById = buildFirebaseAllAvailabilitySessionsById(
        pagedIds,
        sessions,
        effectiveHasLastTable,
        getState().byId.value,
        walkInEnabled,
      );
      const datesById = buildDiscountDatesByIdForFetch(
        pagedIds,
        discountDatesToShowById,
      );

      if (Object.keys(datesById).length === 0) {
        return;
      }

      const abortController = new AbortController();
      actions.setFirebaseAllAvailabilityReplayAbortController(abortController);

      actions.firebaseAllAvailability
        .replayCachedSessionSnapshots({
          restaurantIds: pagedIds,
          sessionsById,
          datesById,
          abortSignal: abortController.signal,
        })
        .then(() => {
          if (
            getState().firebaseAllAvailabilityReplayAbortController ===
            abortController
          ) {
            actions.setFirebaseAllAvailabilityReplayAbortController(undefined);
          }
        });

      return () => {
        const { firebaseAllAvailabilityReplayAbortController } = getState();
        if (
          firebaseAllAvailabilityReplayAbortController !== undefined &&
          !firebaseAllAvailabilityReplayAbortController.signal.aborted
        ) {
          firebaseAllAvailabilityReplayAbortController.abort(
            new AbortError(
              'Canceled "replayFirebaseAllAvailabilitySessionSnapshots" before re-running',
            ),
          );
        }
      };
    },
  ),
  fetchFirebaseAllAvailabilityInView: effectOn(
    [
      (state) => state.inViewById,
      (state) => state.regularOnly.value,
      (state) => state.effectiveDate,
      (state) => state.discountDatesToShowById,
      (state) => state.enabled,
      (state) => state.hasLastTable.value,
      (state) => state.walkIn.enabled,
      (state) => state.firebaseAvailabilitySessions,
    ],
    (actions, change, { getState }) => {
      const inViewById = change.current[0];
      const regularOnly = change.current[1];
      const date = change.current[2];
      const discountDatesToShowById = change.current[3];
      const enabled = change.current[4];
      const hasLastTable = change.current[5];
      const walkInEnabled = change.current[6];
      const sessions = change.current[7];

      if (!enabled) {
        // Clear via sync so live-subscription delta tracking resets with unsubs.
        actions.firebaseAllAvailability.syncLiveSubscriptions({
          restaurantIds: [],
          sessionsById: {},
          datesById: {},
          pathMode: "date",
          abortSignal: new AbortController().signal,
        });
        return;
      }

      const pathMode = getFirebaseAvailabilityPathMode(date);

      // Empty walk-in sessions → tear down live listeners (same as no ids).
      if (sessions.length === 0) {
        actions.firebaseAllAvailability.syncLiveSubscriptions({
          restaurantIds: [],
          sessionsById: {},
          datesById: {},
          pathMode,
          abortSignal: new AbortController().signal,
        });
        return;
      }

      const state = getState();
      const restaurantsById = state.byId.value;
      const rawInViewIds = Object.entries(inViewById).reduce(
        (inViewIdsAcc: number[], [id, inView]) => {
          if (inView) {
            inViewIdsAcc.push(+id);
          }

          return inViewIdsAcc;
        },
        [],
      );
      const inViewIds = rawInViewIds.filter(
        (id) => restaurantsById[id] != null,
      );
      // FT `regularOnly` is inactive on the walk-in path.
      const discountVisible = isDiscountVisible(
        walkInEnabled ? false : regularOnly,
        date,
        discountDatesToShowById,
      );
      const idsToFetch = filterIdsForDiscountVisibility(
        inViewIds,
        discountVisible,
        date,
        discountDatesToShowById,
      );

      if (idsToFetch.length === 0) {
        actions.firebaseAllAvailability.syncLiveSubscriptions({
          restaurantIds: [],
          sessionsById: {},
          datesById: {},
          pathMode,
          abortSignal: new AbortController().signal,
        });
        return;
      }

      const effectiveHasLastTable = walkInEnabled ? false : hasLastTable;
      const sessionsById = buildFirebaseAllAvailabilitySessionsById(
        idsToFetch,
        sessions,
        effectiveHasLastTable,
        restaurantsById,
        walkInEnabled,
      );
      const datesById = buildDiscountDatesByIdForFetch(
        idsToFetch,
        discountDatesToShowById,
      );

      const abortController = new AbortController();
      actions.setFirebaseAllAvailabilityInViewAbortController(abortController);

      logger.log("fetchFirebaseAllAvailabilityInView - idsToFetch", idsToFetch);

      actions.firebaseAllAvailability
        .syncLiveSubscriptions({
          restaurantIds: idsToFetch,
          sessionsById,
          datesById,
          pathMode,
          abortSignal: abortController.signal,
        })
        .then(() => {
          if (
            getState().firebaseAllAvailabilityInViewAbortController ===
            abortController
          ) {
            actions.setFirebaseAllAvailabilityInViewAbortController(undefined);
          }
        });

      return () => {
        const currentState = getState();

        // While the store is enabled, reconcile via the next
        // `syncLiveSubscriptions` desired-set (e.g. in-view scroll diffs).
        // Aborting after the first onValue does not remove Firebase listeners —
        // `subscribe` drops the abort handler then — and races remounts so a
        // clear→add can end with no live subscription.
        if (currentState.enabled) {
          return;
        }

        const { firebaseAllAvailabilityInViewAbortController } = currentState;
        if (
          firebaseAllAvailabilityInViewAbortController !== undefined &&
          !firebaseAllAvailabilityInViewAbortController.signal.aborted
        ) {
          firebaseAllAvailabilityInViewAbortController.abort(
            new AbortError(
              'Canceled "fetchFirebaseAllAvailabilityInView" before re-fetching',
            ),
          );
        }
      };
    },
  ),
  // #endregion -- Firebase all availability

  // #region -- Normalised discount availability
  /**
   * Single derive pass for by-id + next-day maps. Thin selectors below share it.
   * Invalidates on byId/filters; restaurant-scoped skips live in
   * deriveDiscountAvailabilityBundle (see isSameAvailabilitySnapshot pipeline).
   */
  discountAvailabilityBundle: computed(
    [
      (state: State<StoreModel>) => state.deriveCacheIdentity,
      (state: State<StoreModel>) => state.fetchedIds,
      (state: State<StoreModel>) => state.firebaseAllAvailability.byId,
      (state: State<StoreModel>) => state.discountDatesToShowById,
      (state: State<StoreModel>) => state.firebaseAvailabilitySessions,
      (state: State<StoreModel>) =>
        state.walkIn.enabled ? false : state.hasLastTable.value,
      (state: State<StoreModel>) => state.times.start,
      (state: State<StoreModel>) => state.times.end,
      (state: State<StoreModel>) => state.people.value,
      (state: State<StoreModel>) => state.walkIn.enabled,
      (state: State<StoreModel>) => state.byId.value,
    ],
    (
      deriveCacheIdentity,
      fetchedIds,
      firebaseAllAvailabilityById,
      discountDatesToShowById,
      sessions,
      hasLastTable,
      timesStart,
      timesEnd,
      people,
      walkInEnabled,
      restaurantsById,
    ) => {
      return deriveDiscountAvailabilityBundle({
        deriveCacheIdentity,
        restaurantIds: fetchedIds,
        firebaseAllAvailabilityById,
        discountDatesToShowById,
        restaurantsById,
        walkInEnabled,
        context: buildDiscountAvailabilityFilterContext(
          sessions,
          hasLastTable,
          timesStart,
          timesEnd,
          people,
        ),
        strategy: walkInEnabled
          ? walkInDiscountAvailabilityStrategy
          : firstTableDiscountAvailabilityStrategy,
      });
    },
  ),
  discountAvailabilityById: computed(
    [(state: State<StoreModel>) => state.discountAvailabilityBundle],
    (bundle) => bundle.byId,
  ),
  discountAvailabilityByIdOnChange: logEffect("discountAvailabilityById"),
  isNextDiscountDayAvailable: computed(
    [(state: State<StoreModel>) => state.discountAvailabilityBundle],
    (bundle) => bundle.nextDay,
  ),
  isNextDiscountDayAvailableOnChange: logEffect("isNextDiscountDayAvailable"),
  clearDeriveCachesOnFirebaseClear: effectOn(
    [(state) => state.firebaseAllAvailability.byId],
    (_actions, change, { getState }) => {
      const previousById = change.prev[0];
      const currentById = change.current[0];

      if (
        previousById !== undefined &&
        Object.keys(previousById).length > 0 &&
        Object.keys(currentById).length === 0
      ) {
        clearDeriveAvailabilityCaches(getState().deriveCacheIdentity);
      }
    },
  ),
  // #endregion -- Normalised discount availability
  // #endregion - Discount availability

  // #region - Regular availability
  // #region -- All availability search
  allAvailabilitySearch: allAvailabilitySearchModel,
  allAvailabilitySearchAbortController: undefined,
  setAllAvailabilitySearchAbortController: action((state, abortController) => {
    state.allAvailabilitySearchAbortController = abortController;
  }),
  abortAllAvailabilitySearch: thunk((_actions, _payload, { getState }) => {
    const state = getState();
    state.allAvailabilitySearchAbortController?.abort();
  }),
  fetchAllAvailabilitySearch: effectOn(
    [
      (state) => state.fetchedIds,
      (state) => state.people.value,
      (state) => state.date.value,
      (state) => state.discountOnly.value,
      (state) => state.regularOnly.value,
      (state) => state.enabled,
      (state) => state.discoverEnabled,
    ],
    (actions, change, { getState }) => {
      const ids = change.current[0];
      const people = change.current[1];
      const date = change.current[2];
      const discountOnly = change.current[3];
      const regularOnly = change.current[4];
      const enabled = change.current[5];
      const discoverEnabled = change.current[6];

      if (
        !enabled ||
        ids.length === 0 ||
        date === "week" ||
        discountOnly ||
        !discoverEnabled ||
        !regularOnly
      ) {
        return;
      }

      logger.log("fetchAllAvailabilitySearch");

      const abortController = new AbortController();
      actions.setAllAvailabilitySearchAbortController(abortController);

      actions.allAvailabilitySearch
        .fetchAll({
          restaurantIds: ids,
          people,
          date,
          fetchAbortSignal: abortController.signal,
        })
        .then(() => {
          actions.setAllAvailabilitySearchAbortController(undefined);
        })
        .catch((error: unknown) => {
          // suppress abort errors as we're intentionally aborting the request
          if (!isAbortError(error)) {
            throw error;
          }
        });

      return () => {
        const { allAvailabilitySearchAbortController } = getState();
        if (
          allAvailabilitySearchAbortController !== undefined &&
          !allAvailabilitySearchAbortController.signal.aborted
        ) {
          allAvailabilitySearchAbortController.abort(
            new AbortError(
              'Canceled "fetchAllAvailabilitySearch" before re-fetching',
            ),
          );
        }
      };
    },
  ),
  filteredAllAvailabilitySearchById: computed((state) => {
    const datesToShow =
      state.date.value === "week"
        ? state.regularDatesToShow
        : [state.date.value];

    const entries = Object.entries(state.allAvailabilitySearch.byId);

    const filtered = entries.map(([restaurantId, searchAvailabilityByDate]) => {
      if (!searchAvailabilityByDate) {
        return [restaurantId];
      }

      const regularAvailabilityByDate = datesToShow.reduce(
        (acc: ByDate<RegularAvailabilitySlotType[]>, date) => {
          const searchAvailability =
            searchAvailabilityByDate[date]?.[state.people.value];

          if (!searchAvailability) {
            return acc;
          }

          const sessionSlots = (searchAvailability.availableTimes || []).filter(
            (slot): slot is AvailabilitySearchSlotWithOptions =>
              slot !== null &&
              slot.session !== null &&
              sessionsMatch(slot.session, state.session.value),
          );

          const timeFilteredSlots = sessionSlots.filter((slot) =>
            isInTime(slot.time, state.times.start, state.times.end),
          );

          acc[date] = timeFilteredSlots.map((slot) => ({
            ...slot,
            available: !!slot.available,
            cutOffTime: slot.cutOffTime || undefined,
            deal: slot.deal || undefined,
            dealDescription: slot.dealDescription || undefined,
            dataStatus: searchAvailability.dataStatus,
            syncExpires: searchAvailability.syncExpires || undefined,
            leaveTime: slot.leaveTime || undefined,
            session: slot.session as SessionType,
          }));

          return acc;
        },
        {},
      );

      return [restaurantId, regularAvailabilityByDate];
    });

    return Object.fromEntries(filtered);
  }),
  filteredAllAvailabilitySearchByIdOnChange: logEffect(
    "filteredAllAvailabilitySearchById",
  ),
  // #endregion -- All availability search

  // #region -- Firebase regular table sync
  firebaseRegularTableSync: firebaseRegularTableSyncModel,
  firebaseRegularTableSyncAbortController: undefined,
  setFirebaseRegularTableSyncAbortController: action(
    (state, abortController) => {
      state.firebaseRegularTableSyncAbortController = abortController;
    },
  ),
  abortFirebaseRegularTableSync: thunk((_actions, _payload, { getState }) => {
    const state = getState();
    state.firebaseRegularTableSyncAbortController?.abort();
  }),
  fetchFirebaseRegularTableSync: effectOn(
    [
      (state) => state.fetchedIds,
      (state) => state.date.value,
      (state) => state.discountOnly.value,
      (state) => state.regularOnly.value,
      (state) => state.people.value,
      (state) => state.enabled,
      (state) => state.regularDatesToShow,
      (state) => state.discoverEnabled,
      (state) => state.inViewById,
    ],
    (actions, change, { getState }) => {
      const ids = change.current[0];
      const date = change.current[1];
      const discountOnly = change.current[2];
      const regularOnly = change.current[3];
      const people = change.current[4];
      const enabled = change.current[5];
      const regularDatesToShow = change.current[6];
      const discoverEnabled = change.current[7];
      const inViewById = change.current[8];

      // only fetch on the week view, when viewing regular tables
      if (
        !enabled ||
        date !== "week" ||
        discountOnly ||
        !discoverEnabled ||
        !regularOnly
      ) {
        return;
      }

      logger.log("fetchFirebaseRegularTableSync");

      const state = getState();
      const firebaseRegularTableSyncById = state.firebaseRegularTableSync.byId;

      const missingIds = ids.filter((id) => !firebaseRegularTableSyncById[id]);

      const inViewIds = Object.entries(inViewById).reduce(
        (inViewIdsAcc: number[], [id, inView]) => {
          if (inView) {
            inViewIdsAcc.push(+id);
          }

          return inViewIdsAcc;
        },
        [],
      );

      const allIds = union(missingIds, inViewIds);

      const onlyOnceIds = difference(missingIds, inViewIds);

      const onlyOnceById = onlyOnceIds.reduce(
        (onlyOnceByIdAcc: ByRestaurantId<boolean>, id) => {
          onlyOnceByIdAcc[id] = true;

          return onlyOnceByIdAcc;
        },
        {},
      );

      const datesToShow = regularDatesToShow;

      const abortController = new AbortController();
      actions.setFirebaseRegularTableSyncAbortController(abortController);

      actions.firebaseRegularTableSync
        .fetch({
          restaurantIds: allIds,
          dates: date === "week" ? datesToShow : [date],
          people,
          onlyOnceById,
          abortSignal: abortController.signal,
        })
        .then(() => {
          actions.setFirebaseRegularTableSyncAbortController(undefined);
        });

      return () => {
        const { firebaseRegularTableSyncAbortController } = getState();
        if (
          firebaseRegularTableSyncAbortController !== undefined &&
          !firebaseRegularTableSyncAbortController.signal.aborted
        ) {
          firebaseRegularTableSyncAbortController.abort(
            new AbortError(
              'Canceled "fetchFirebaseRegularTableSync" before re-fetching',
            ),
          );
        }
      };
    },
  ),
  filteredFirebaseRegularTableSyncById: computed((state) => {
    const datesToShow = state.regularDatesToShow;

    const entries = Object.entries(state.firebaseRegularTableSync.byId);

    const filtered = entries.map(([restaurantId, firebaseRegularTableSync]) => {
      if (!firebaseRegularTableSync) {
        return [restaurantId];
      }

      const availabilityByDate = datesToShow.reduce(
        (slotsByDate: RegularAvailabilityByDate, date) => {
          slotsByDate[date] = undefined; // default to "nothing found" for the date (to allow fetching via allAvailabilitySearch API)

          const dateAvailability = firebaseRegularTableSync[date];

          if (!dateAvailability) {
            return slotsByDate;
          }

          const regularTableSyncSlot = dateAvailability[state.people.value];

          // not yet fetched
          if (regularTableSyncSlot === undefined) {
            slotsByDate[date] = undefined;

            return slotsByDate;
          }

          // fetched but nothing found
          if (regularTableSyncSlot === null) {
            slotsByDate[date] = null;

            return slotsByDate;
          }

          const sessionSlots = (
            regularTableSyncSlot.availableTimes || []
          ).filter(
            (slot) =>
              slot.session !== null &&
              sessionsMatch(slot.session, state.session.value),
          );

          const timeFilteredSlots = sessionSlots.filter((slot) =>
            isInTime(slot.time, state.times.start, state.times.end),
          );

          slotsByDate[date] = timeFilteredSlots.map((slot) => ({
            ...slot,
            id: regularTableSyncSlot.id,
            available: !!slot.available,
            cutOffTime: slot.cutOffTime || undefined,
            leaveTime: slot.leaveTime || undefined,
            deal: slot.deal || undefined,
            dealDescription: slot.dealDescription || undefined,
            session:
              (slot.session as SessionType) || // TODO: verify session is of SessionType
              undefined,
            dataStatus: regularTableSyncSlot.dataStatus,
            syncExpires: regularTableSyncSlot.syncExpires || undefined,
          }));

          return slotsByDate;
        },
        {},
      );

      return [restaurantId, availabilityByDate];
    });

    return Object.fromEntries(filtered);
  }),
  filteredFirebaseRegularTableSyncByIdOnChange: logEffect(
    "filteredFirebaseRegularTableSyncById",
  ),
  // #endregion -- Firebase regular table sync

  // #region -- Normalised regular availability
  regularAvailabilityById: computed((state) =>
    state.fetchedIds.reduce((availabilityById: RegularAvailabilityById, id) => {
      const allAvailabilitySearch = state.filteredAllAvailabilitySearchById[id];
      const firebaseRegularTableSync =
        state.filteredFirebaseRegularTableSyncById[id];
      const datesToShow =
        state.date.value === "week"
          ? state.regularDatesToShow
          : [state.date.value];

      availabilityById[id] = datesToShow.reduce(
        (availabilityByDate: RegularAvailabilityByDate, date) => {
          const searchDateAvailability = allAvailabilitySearch?.[date];
          const firebaseDateAvailability = firebaseRegularTableSync?.[date];

          const dateAvailability =
            searchDateAvailability !== undefined
              ? searchDateAvailability
              : firebaseDateAvailability;

          availabilityByDate[date] = dateAvailability;

          return availabilityByDate;
        },
        {},
      );

      return availabilityById;
    }, {}),
  ),
  regularAvailabilityByIdOnChange: logEffect("regularAvailabilityById"),
  // #endregion -- Normalised regular availability
  // #endregion - Regular availabilty

  // #region - Restaurant IDs to show
  idsToShow: computed((state) => {
    const ids: number[] = [];

    for (const id of state.pagedIds) {
      if (state.byId.value[id] === undefined) {
        break;
      }
      // continue if the restaurant failed to load
      if (state.byId.value[id] === null) {
        continue;
      }
      let includeId = false;
      if (state.walkIn.enabled) {
        includeId = shouldShowWalkInDiscountSection(
          state.byId.value,
          state.discountAvailabilityById,
          state.effectiveDate,
          state.times.isSet,
          state.times.start,
          state.times.end,
          state.discountDatesToShowById,
          id,
          state.mode === "single",
          state.firebaseAllAvailability.loading,
          state.skipInitialFirebaseFetch,
          state.people.value,
        );
      } else {
        const filtersApplied: boolean = !!(
          state.times.isSet ||
          state.tagIds.value.length > 0 ||
          state.suburb.ids.length > 0 ||
          state.favourite.value
        );

        const showDiscountSection = shouldShowDiscountSection(
          state.regularOnly.value,
          state.byId.value,
          state.hasLastTable.value,
          state.discountAvailabilityById,
          state.effectiveDate,
          state.firebaseAvailabilitySessions,
          state.times.isSet,
          state.times.start,
          state.times.end,
          state.discountDatesToShowById,
          state.discountOnly.value,
          id,
          state.mode === "single",
          state.firebaseAllAvailability.loading,
          state.skipInitialFirebaseFetch,
          state.people.value,
          state.sort.value,
          filtersApplied,
        );
        includeId = showDiscountSection;
      }

      if (includeId) {
        ids.push(id);
      }
    }
    return ids;
  }),
  idsToShowOnChange: logEffect("idsToShow"),
  /** Drop in-view flags for restaurants that left the current paged id set. */
  pruneStaleInViewByPagedIds: effectOn(
    [(state) => state.pagedIds, (state) => state.mode],
    (actions, change, { getState }) => {
      if (change.current[1] === "map") {
        return;
      }

      const pagedIdSet = new Set(change.current[0]);
      const staleInView = Object.entries(getState().inViewById).flatMap(
        ([id, inView]) =>
          inView && !pagedIdSet.has(Number(id))
            ? [{ restaurantId: Number(id), inView: false as const }]
            : [],
      );

      if (staleInView.length > 0) {
        actions.mergeInView(staleInView);
      }
    },
  ),
  // #endregion - Restaurant IDs to show

  // #region - Restaurants to show
  // Restaurants to show (with details and availability).
  restaurantsToShow: computed((state) =>
    state.idsToShow.reduce((restaurantsToShow: RestaurantToShow[], id) => {
      const details = state.byId.value[id];

      if (details) {
        restaurantsToShow.push({
          ...details,
          discountAvailability: state.discountAvailabilityById[id],
          isNextDiscountDayAvailable:
            state.isNextDiscountDayAvailable[id] || false,
          regularAvailability: state.regularAvailabilityById[id],
        });
      }

      return restaurantsToShow;
    }, []),
  ),
  restaurantsToShowOnChange: logEffect("restaurantsToShow"),
  // #endregion - Restaurants to show

  // #region - Loading states
  loading: computed(
    (state) =>
      state.allIds.loading ||
      state.byId.loading ||
      state.allAvailabilitySearch.loading ||
      state.firebaseRegularTableSync.loading ||
      state.firebaseAllAvailability.loading ||
      // Server-rendered markup gets the initial state, so without this hosts
      // show "no results" before any fetch starts. `single` never fetches
      // `allIds`, so including it would strand.
      (state.mode !== "single" && !state.allIds.hasSettled),
  ),
  loadingOnChange: logEffect("loading"),
  // #endregion - Loading states

  // #region - Paging
  page: pageModel,
  hasNextPage: computed((state) => {
    const pageEnd = state.page.limit;
    const idsCount = state.allIds.value.length;
    return pageEnd < idsCount;
  }),
  // Automatically fetch paged results until we have enough results, or we've fetched everything
  fetchUntilResults: effectOn(
    [
      (state) => state.loading,
      (state) => state.idsToShow.length,
      (state) => state.hasNextPage,
      (state) => state.enabled,
      (state) => state.mode,
    ],
    (actions, _change, { getState }) => {
      // add some delay before increasing the limit to prevent quick successive changes causing multiple increases witohut giving the fetching a moment to start
      wait(50).then(() => {
        const state = getState();

        const finishedLoading = !state.loading;
        const enoughResults = state.idsToShow.length >= MINIMUM_RESULTS;
        const moreToFetch = state.hasNextPage;
        const isList = state.mode === "list";
        const isEnabled = state.enabled;

        if (!finishedLoading || !isList || !isEnabled) {
          return;
        }

        // Automatic doubling is part of an open initial load. Once nextPage
        // has frozen the sample, settle ignores further limit changes, so
        // doubling here still fills a short page without joining that sample.
        if (!enoughResults && moreToFetch) {
          actions.page.setLimit(state.page.limit * 2); // progressively fetch larger pages to more quickly resolve cases where there aren't any results
          return;
        }

        // A fetch that failed off-list never opened an attempt, and entering
        // list starts one. Settling that attempt would report the failure as
        // an empty success.
        if (state.allIds.error) {
          failListInitialLoad(state.deriveCacheIdentity);
          return;
        }

        settleListInitialLoad(state);
      });
    },
  ),
  // #endregion - Paging
};

export default model;
